import type {
	ConceptDefinitionDTO,
	ParameterizedConceptDTO,
	ParameterizedConceptDTORelativeness,
	RiskModelExposureLevels,
} from "@eu.mdotm.api/smart-workspaces";
import { typedObjectEntries, typedObjectKeys } from "@mdotm/shadcn/legacy/utils";
import type { HierarchicalOption } from "@mdotm/shadcn/mdotm/components";
import equal from "fast-deep-equal";
import type { ConceptParameterForm } from "../chart-type";
import { createSelfRelativenessLabel, nameDropsConceptLabel } from "../common";
import { takesRelativeness } from "../representation-mode";
import { isConceptCustomExposureTag } from "../repository";
import { riskModelLevelLabels } from "./breakdown";
import type { WorkspaceChain } from "./type";

/**
 * The rules behind the inline chain: which words the generated name is composed of, which of them are
 * controls, what the trailing select may add, and how a risk-model level pin moves. Data in, data
 * out — `InlineConceptChain.tsx` renders them and the density chart's connected control fetches the lists
 * and persists the patches.
 */

/** The id the risk-model concept is published under; the one concept whose levels are a chain of pins. */
export const riskModelConceptId = "systemId: riskModel";

// --- Risk-model pins -----------------------------------------------------------------------------

/** One of the five risk-model levels. */
export type RiskModelLevel = keyof typeof riskModelLevelLabels;

/** A level pinned to one of its values. */
export type RiskModelPin = { key: RiskModelLevel; value: string };

/** The five levels in canonical order. */
const riskModelLevels: ReadonlyArray<RiskModelLevel> = typedObjectKeys(riskModelLevelLabels);

/**
 * The pins in the order the stored `parameters` lists them: key insertion order is what
 * `autoGenerateMetricName` joins, so it is read back in that order and rebuilt from scratch on every change.
 */
export function pinsFromParameters(parameters: RiskModelExposureLevels | undefined): RiskModelPin[] {
	return typedObjectEntries(parameters ?? {}).flatMap(([key, value]) =>
		value && (riskModelLevels as ReadonlyArray<string>).includes(key) ? [{ key, value }] : [],
	);
}

/** A fresh object whose key order is the pin order. Never spread the previous `parameters` over it. */
export function parametersFromPins(pins: ReadonlyArray<RiskModelPin>): RiskModelExposureLevels {
	const parameters: RiskModelExposureLevels = {};
	for (const pin of pins) {
		parameters[pin.key] = pin.value;
	}
	return parameters;
}

/** The combinations every pin holds on. */
export function combinationsUnder(
	combinations: ReadonlyArray<RiskModelExposureLevels>,
	pins: ReadonlyArray<RiskModelPin>,
): RiskModelExposureLevels[] {
	return combinations.filter((row) => pins.every((pin) => row[pin.key] === pin.value));
}

/**
 * The levels pinned nowhere that still have a value in `rows`, in canonical order. Empty means nothing can
 * be added: the trailing select is absent, not disabled.
 */
export function freeLevelsWithValues(
	rows: ReadonlyArray<RiskModelExposureLevels>,
	pins: ReadonlyArray<RiskModelPin>,
): RiskModelLevel[] {
	return riskModelLevels.filter((level) => !pins.some((pin) => pin.key === level) && rows.some((row) => row[level]));
}

/**
 * Keep-if-valid: slot `index` takes `pin`, and every later pin that still has a row under the new pins
 * stays where it was. The ones that do not are dropped silently.
 */
export function changePin(
	combinations: ReadonlyArray<RiskModelExposureLevels>,
	pins: ReadonlyArray<RiskModelPin>,
	index: number,
	pin: RiskModelPin,
): RiskModelPin[] {
	const kept = [...pins.slice(0, index), pin];
	for (const later of pins.slice(index + 1)) {
		// The level that just moved into this slot: a level is pinned at most once.
		if (later.key === pin.key) {
			continue;
		}
		if (combinationsUnder(combinations, [...kept, later]).length > 0) {
			kept.push(later);
		}
	}
	return kept;
}

/** Removes one pin; the later ones stay, in order. Removing the last one is allowed. */
export function removePin(pins: ReadonlyArray<RiskModelPin>, index: number): RiskModelPin[] {
	return pins.filter((_, i) => i !== index);
}

// --- The chain's vocabulary ----------------------------------------------------------------------

/** The first series' current fields, as the connected piece hands them over. */
export type InlineConceptSeries = Pick<
	ParameterizedConceptDTO,
	"conceptId" | "timeHorizon" | "representationMode" | "relativeness" | "parameters"
>;

/**
 * What a word emits: the metric's own parameters, and the `conceptId` a tag word switches to. An absent
 * key leaves that field alone, and the `parameters` object is always a fresh one — never a merge over the
 * previous. The metric's type and what it is compared against are the Edit Chart panel's, so no word here
 * emits them.
 */
export type InlineConceptPatch = Partial<Pick<ParameterizedConceptDTO, "conceptId" | "timeHorizon">> & {
	parameters?: WorkspaceChain["parameters"];
};

/** Whatever the caller has loaded; `undefined` means "not loaded, or not needed by this concept". */
export type InlineConceptChainData = {
	combinations?: ReadonlyArray<RiskModelExposureLevels>;
	tagConcepts?: ReadonlyArray<ConceptDefinitionDTO>;
};

/**
 * One word of the generated name. Only some are controls: the metric's own parameters. The concept's
 * label and the relativeness pair the name carries — "Rel to" and the entity — are words alone, edited
 * from the Edit Chart panel.
 */
export type InlineWord =
	| { kind: "conceptLabel" | "metricType" | "entity" }
	| { kind: "timeHorizon" | "tagOption" | "riskModel"; live: boolean };

/** Whether the word has a list behind it, rather than being text the name merely carries. */
export function isLiveWord(word: InlineWord): boolean {
	return "live" in word && word.live;
}

/** A tag concept, or one of its exposure options. */
export type TagOptionValue = { id: string; opt?: string };

/** The chain's vocabulary for the DTO's loosely typed `parameters`, as `formFromConcept` reads it. */
export function chainParameters(series: InlineConceptSeries): WorkspaceChain["parameters"] {
	return series.parameters as WorkspaceChain["parameters"];
}

// --- Option lists --------------------------------------------------------------------------------

/** The entity word, exactly as the generated name spells it. */
export function relativenessWordLabel(relativeness: ParameterizedConceptDTORelativeness): string {
	return relativeness.uuid === undefined ? createSelfRelativenessLabel(relativeness) : (relativeness.name ?? "");
}

/** One node per tag concept, its options as leaves. No "All" leaf: the header pins, it does not expand. */
export function tagExposureOptions(
	tagConcepts: ReadonlyArray<ConceptDefinitionDTO> | undefined,
): HierarchicalOption<TagOptionValue>[] {
	return (tagConcepts ?? []).map((tag) => ({
		label: tag.defaultLabel!,
		value: { id: tag.id! },
		children: (tag.exposureOptions ?? []).map((opt) => ({ label: opt, value: { id: tag.id!, opt } })),
	}));
}

// --- What the header carries ---------------------------------------------------------------------

/** The words of `autoGenerateMetricName`, in the order it joins them. */
export function inlineChainWords(
	concept: ConceptDefinitionDTO,
	form: ConceptParameterForm,
	series: InlineConceptSeries,
	data: InlineConceptChainData,
): InlineWord[] {
	const parameters = chainParameters(series);
	const pins = pinsFromParameters(parameters);
	const isRiskModel = concept.id === riskModelConceptId;
	const words: InlineWord[] = [];
	// Asked of the name generator rather than decided here: the title *is* the generated name, so the one
	// thing it must never do is disagree with it about which words the name has.
	if (!nameDropsConceptLabel(concept, series.parameters)) {
		words.push({ kind: "conceptLabel" });
	}
	if (series.timeHorizon) {
		words.push({ kind: "timeHorizon", live: (form.allowedTimeHorizons?.length ?? 0) > 0 });
	}
	if (takesRelativeness(series.representationMode) && series.relativeness) {
		words.push({ kind: "metricType" }, { kind: "entity" });
	}
	if (parameters?.tagExposureOption) {
		words.push({
			kind: "tagOption",
			live: isConceptCustomExposureTag(series.conceptId) && (data.tagConcepts?.length ?? 0) > 0,
		});
	}
	if (isRiskModel && pins.length > 0) {
		words.push({ kind: "riskModel", live: data.combinations != null });
	}
	return words;
}

/** The levels the trailing select may add: the ones pinned nowhere that still have a value. */
export function inlineChainAddableLevels(
	concept: ConceptDefinitionDTO,
	series: InlineConceptSeries,
	data: InlineConceptChainData,
): RiskModelLevel[] {
	const pins = pinsFromParameters(chainParameters(series));
	return concept.id === riskModelConceptId && data.combinations
		? freeLevelsWithValues(combinationsUnder(data.combinations, pins), pins)
		: [];
}

/** Whether the header has anything to click with the data it holds now — the title's quoted fallback. */
export function inlineChainIsLive(
	concept: ConceptDefinitionDTO,
	form: ConceptParameterForm,
	series: InlineConceptSeries,
	data: InlineConceptChainData,
): boolean {
	return (
		inlineChainAddableLevels(concept, series, data).length > 0 ||
		inlineChainWords(concept, form, series, data).some(isLiveWord)
	);
}

/**
 * The data-free superset of {@link inlineChainIsLive}: whether it is worth mounting the control and
 * fetching what its words need at all.
 */
export function inlineChainCanBeLive(
	concept: ConceptDefinitionDTO,
	form: ConceptParameterForm,
	series: InlineConceptSeries,
): boolean {
	return (
		concept.id === riskModelConceptId ||
		isConceptCustomExposureTag(series.conceptId) ||
		Boolean(series.timeHorizon && (form.allowedTimeHorizons?.length ?? 0) > 0)
	);
}

/**
 * Whether the compact row has to name the metric itself.
 *
 * Under a title the user typed, nothing else says which metric the steps belong to, so the row leads with
 * the concept's label — except where a step already carries it. Two do: the risk model's pins stand in for
 * the name exactly as they do in the generated title, and the tag step's trigger reads `Tag / Option`,
 * which is the label again. That trigger falls back to its placeholder when the stored option is not one
 * the tag publishes, and then the name is still the row's to say.
 */
export function inlineRowNamesConcept(
	concept: ConceptDefinitionDTO,
	form: ConceptParameterForm,
	series: InlineConceptSeries,
	data: InlineConceptChainData,
): boolean {
	if (!inlineChainWords(concept, form, series, data).some((word) => word.kind === "conceptLabel")) {
		return false;
	}
	const option = chainParameters(series)?.tagExposureOption;
	const tagStepNamesIt =
		option != null &&
		isConceptCustomExposureTag(series.conceptId) &&
		(data.tagConcepts ?? []).some((tag) => tag.id === series.conceptId && (tag.exposureOptions ?? []).includes(option));
	return !tagStepNamesIt;
}

/** A field as the document carries it: `undefined` and absent are the same on the wire. */
function wireValue(value: unknown): unknown {
	return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

/**
 * The caller's `onChange` minus the patches that would leave the document as it is — both selects report a
 * re-pick of the current option as a change, and a re-pick must not persist.
 */
export function changesOnly(
	series: InlineConceptSeries,
	onChange: (patch: InlineConceptPatch) => void,
): (patch: InlineConceptPatch) => void {
	return (patch) => {
		const keys = Object.keys(patch) as Array<keyof InlineConceptPatch>;
		if (keys.some((key) => !equal(wireValue(patch[key]), wireValue(series[key])))) {
			onChange(patch);
		}
	};
}
